import React, { useEffect, useState, useRef } from 'react';
import Wrapper from '../../components/Wrapper';
import { Paper, Typography, Box, Divider, Button, IconButton, Alert } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { shortFunction, rate } from '../../translate';
import Api from "../../Api";
import { useNavigate } from 'react-router-dom';
import FileUploader from '../../components/new_component/FileUploader';
import { toast } from 'react-toastify';
import { REACT_APP_API_URL } from "../../config";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

const { REACT_APP_SERVER } = process.env;
const color = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : '#7ebc0a';
const hoverColor = REACT_APP_SERVER === 'ATS' ? '#095a8f' : '#6aa507';

export default function MusicHold() {
    const langIndex = +localStorage.getItem('atsLang') || 1;
    const [musicHoldFile, setMusicHoldFile] = useState(null);
    const [fileTarget, setFileTarget] = useState('musichold');
    const [fileModalOpen, setFileModalOpen] = useState(false);
    const [fileModalLoading, setFileModalLoading] = useState(false);
    const [files, setFiles] = useState([]);
    const navigate = useNavigate();
    const audioRef = useRef(null);
    const playingIdRef = useRef(null);
    const [playingId, setPlayingId] = useState(null);
    const keySuffix = '&key=akNwNXN1U2k4bzdEQ09QU29KMUpYbXFiQTV5c3FmUm1MOUNheW5qWlYwOD0=';
    
    const getAudioSrc = (f) => {
        const fid = f?.fname || f?.id || f?.name;
        return fid ? `${REACT_APP_API_URL}/rest/files/audio?file=${fid}${keySuffix}` : null;
    };
    
    const stopAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        setPlayingId(null);
        playingIdRef.current = null;
    };
    
    useEffect(() => {
        (async () => {
            try {
                const { data } = await Api.fileGet(true);
                setFiles(data?.file || []);
            } catch (e) {
                console.log(e);
            }
        })();
    }, []);
    
    useEffect(() => {
        if (!musicHoldFile) {
            const foundMusicHold = files.find(f => Number(f.mhold) === 1);
            if (foundMusicHold) {
                setMusicHoldFile(foundMusicHold);
            }
        }
    }, [files, musicHoldFile]);
    
    const handleFileSave = async (file) => {
        try {
            setFileModalLoading(true);
            const selected = file || musicHoldFile;
            let fid = selected?.id || selected?.fname;
            if (!fid && selected && selected.size) {
                const payload = {
                    file: selected,
                    name: selected.name || 'file',
                    size: selected.size || 0,
                };
                const { data: createData } = await Api.fileCreate(payload);
                const created = createData?.file?.[createData.file.length - 1];
                if (created) {
                    fid = created.id || created.fname;
                    setMusicHoldFile(created);
                }
            }
            if (!fid) {
                setFileModalOpen(false);
                return;
            }
            await Api.fileEdit(fid, 1, 'musichold');
            const { data } = await Api.fileGet(true);
            setFiles(data?.file || []);
            toast.success(shortFunction.operator_save?.[langIndex] || 'Saved');
            setFileModalOpen(false);
        } catch (e) {
            console.log(e);
        } finally {
            setFileModalLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setFileModalLoading(true);
            await Api.fileEdit(musicHoldFile.id, 1, 'musichold');
            const { data } = await Api.fileGet(true);
            setFiles(data?.file || []);
            toast.success(shortFunction.operator_save?.[langIndex] || 'Saved');
            setFileModalOpen(false);
        } catch (e) {
            console.log(e);
        } finally {
            setFileModalLoading(false);
        }
    }
    const musicHoldLabel = shortFunction.musicHold?.[langIndex] || 'Music Hold';

    return (
        <Wrapper>
            <div className="tableParent">
                <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.5, }}>
                        <IconButton
                            size="small"
                            sx={{ color, fontSize: 18, '&:hover': { backgroundColor: `${color}15` } }}
                            onClick={() => navigate(-1)}
                            aria-label="Back"
                        >
                            <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
                        </IconButton>
                        <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
                            {musicHoldLabel}
                        </Typography>
                    </Box>

                    <Alert
                        icon={<InfoIcon />}
                        severity="success"
                        sx={
                            REACT_APP_SERVER !== 'ATS'
                                ? { mb: 3 }
                                : {
                                    mb: 3,
                                    border: `1px solid ${color}`,
                                    backgroundColor: `${color}10`,
                                    '& .MuiAlert-icon': { color },
                                }
                        }
                    >
                        <Typography variant="body2">
                            {shortFunction.musicHoldDescription?.[langIndex] || 'Music Hold plays background music to callers while they are waiting for an employee or during call transfer.'}
                        </Typography>
                    </Alert>

                    <Divider />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, minHeight: 160 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                {musicHoldLabel}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    sx={{ textTransform: 'none', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                                >
                                    {musicHoldFile ? musicHoldFile.name : (rate.choose_comp?.[langIndex] || 'Choose from computer')}
                                    <input
                                        type="file"
                                        hidden
                                        accept=".wav,.mp3,audio/wav,audio/mpeg"
                                        onChange={async (ev) => {
                                            const file = ev.target.files?.[0] || null;
                                            if (file) {
                                                const allowed = ['audio/wav', 'audio/x-wav', 'audio/mpeg', 'audio/mp3', 'audio/wave', 'audio/vnd.wave'];
                                                const maxSize = 5 * 1024 * 1024;
                                                if (!allowed.includes(file.type) || file.size > maxSize) {
                                                    toast.error(rate.file_limit?.[langIndex] || 'Only mp3/wav files up to 5MB are allowed.');
                                                    ev.target.value = '';
                                                    return;
                                                }
                                            }
                                            setMusicHoldFile(file);
                                            if (file) {
                                                setFileTarget('musichold');
                                                await handleFileSave(file);
                                            }
                                        }}
                                    />
                                </Button>
                                <Button
                                    variant="outlined"
                                    sx={{ textTransform: 'none', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                                    onClick={() => { setFileTarget('musichold'); setFileModalOpen(true); }}
                                >
                                    {rate.choose_file?.[langIndex] || 'Choose from existing files'}
                                </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 2 }}>
                                {rate.file_limit?.[langIndex] || 'Only mp3/wav files up to 5MB are allowed.'}
                            </Typography>
                            {musicHoldFile && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {musicHoldFile ? (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            sx={{ minWidth: 36, padding: '2px 8px', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                                            onClick={async () => {
                                                const fid = musicHoldFile.fname || musicHoldFile.id || musicHoldFile.name;
                                                if (!fid) return;
                                                if (playingId === fid) {
                                                    stopAudio();
                                                    return;
                                                }
                                                try {
                                                    stopAudio();
                                                    const src = getAudioSrc(musicHoldFile);
                                                    if (!src) return;
                                                    const audio = new Audio(src);
                                                    audioRef.current = audio;
                                                    playingIdRef.current = fid;
                                                    setPlayingId(fid);
                                                    audio.onended = () => {
                                                        stopAudio();
                                                    };
                                                    audio.onerror = () => {
                                                        stopAudio();
                                                    };
                                                    await audio.play();
                                                } catch (e) {
                                                    console.log(e);   
                                                    stopAudio();
                                                }
                                            }}
                                        >
                                            {playingId === (musicHoldFile.fname || musicHoldFile.id || musicHoldFile.name) ? '■' : '▶'}
                                        </Button>
                                    ) : null}
                                    <Typography variant="body2" color="text.secondary">{musicHoldFile.name}</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            variant="outlined"
                            sx={{ textTransform: 'none', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                            onClick={() => { 
                                navigate('/short_function');
                                setMusicHoldFile(null);
                            }}
                        >
                            {shortFunction.operator_close?.[langIndex] || 'Cancel'}
                        </Button>
                        <Button
                            variant="contained"
                            sx={{ textTransform: 'none', backgroundColor: color, color: '#fff', '&:hover': { backgroundColor: hoverColor } }}
                            onClick={() => 
                                handleSave()
                            }
                        >
                            {shortFunction.operator_save?.[langIndex] || 'Save'}
                        </Button>
                    </Box>
                </Paper>
                <FileUploader
                    open={fileModalOpen}
                    onClose={() => setFileModalOpen(false)}
                    files={files}
                    loading={fileModalLoading}
                    target={fileTarget}
                    selectedId={musicHoldFile?.id || musicHoldFile?.fname || musicHoldFile?.name}
                    onUpload={(f) => {
                        setMusicHoldFile(f);
                    }}
                    onSelect={(f) => {
                        setMusicHoldFile(f);
                    }}
                    onSave={handleFileSave}
                />
            </div>
        </Wrapper>
    )
}
